<script>
/**
 * @component PatientQuickForm
 * @description Mini-form de creación de paciente con campos esenciales,
 *              pensado para flows inline (caso UC-REG-130: crear desde el
 *              modal de asignación de turno cuando la búsqueda no
 *              encuentra al paciente). Reusa `patientService.create`
 *              (mismo endpoint que el form completo); el backend ya
 *              maneja find-or-associate (UC-REG-118) y username
 *              cross-org (UC-REG-117).
 *
 *              Eventos:
 *              - `@created(patient)` — payload del paciente creado o
 *                asociado (con `existed: true` si reutilizó uno).
 *              - `@cancel` — el usuario quiere volver al estado previo
 *                sin crear nada.
 *
 * @example
 *   <PatientQuickForm
 *     :prefill-name="searchInput"
 *     @created="handlePatientCreated"
 *     @cancel="creating = false"
 *   />
 */
import patientService from "@/services/patient";
import {
  QUICK_FORM_DOC_TYPES,
  QUICK_FORM_GENDERS,
  buildQuickPatientPayload,
} from "@/services/patient-quick-form";
import { ValidationObserver, ValidationProvider } from "vee-validate";
// UC-REG-317: búsqueda en el padrón del Municipio para autocompletar el alta.
import {
  hasPadronStaffLookupCapability,
  normalizeDni,
  lookupByDni,
  lookupErrorMessage,
  personLabel,
  personToQuickForm,
} from "@/services/patient-padron";

export default {
  name: "PatientQuickForm",

  components: {
    ValidationObserver,
    ValidationProvider,
  },

  props: {
    /**
     * @description Texto que el médico ya tenía tipeado en el buscador;
     *              lo dejamos para que el médico no lo pierda. No mapea
     *              a un field específico — el form arranca limpio.
     */
    prefillName: { type: String, default: "" },
  },

  data() {
    return {
      isSubmitting: false,
      patient: {
        firstName: "",
        lastName: "",
        governmentId: { type: "dni", number: "" },
        bornAt: null,
        gender: "",
        email: "",
        phone: "",
      },
      docTypes: QUICK_FORM_DOC_TYPES,
      genders: QUICK_FORM_GENDERS,
      /** @type {{searching:boolean, persons:Object[], selected:number|null, applied:boolean}} UC-REG-317 */
      padron: { searching: false, persons: [], selected: null, applied: false },
    };
  },

  computed: {
    /**
     * @description UC-REG-317 — La API expone la capacidad (perfil del tenant +
     *   credenciales del padrón cargadas).
     * @returns {boolean}
     */
    padronLookupEnabled() {
      return hasPadronStaffLookupCapability(
        this.$store && this.$store.state.organizationContext
      );
    },
  },

  methods: {
    /**
     * @description Submit del form. Construye el payload y delega en
     *              patientService.create. Maneja `existed` y errores
     *              comunes con toasts.
     * @returns {Promise<void>}
     */
    async submit() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;
      this.isSubmitting = true;
      try {
        const payload = buildQuickPatientPayload(this.patient);
        const created = await patientService.create(payload);
        this.$message({
          type: "success",
          message: "Paciente asociado a tu lista.",
          duration: 3000,
        });
        this.$emit("created", created);
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "No se pudo crear el paciente.";
        this.$message({ type: "error", message: msg, duration: 5000 });
      } finally {
        this.isSubmitting = false;
      }
    },

    cancel() {
      this.$emit("cancel");
    },

    /**
     * @description UC-REG-317 — Busca el DNI tipeado en el padrón del Municipio.
     */
    async searchPadron() {
      const dni = normalizeDni(this.patient.governmentId.number);
      if (!dni) {
        this.$message({
          type: "warning",
          message: "Ingresá un DNI válido (7 u 8 dígitos).",
        });
        return;
      }
      this.padron.searching = true;
      this.padron.persons = [];
      this.padron.selected = null;
      try {
        const { persons } = await lookupByDni(dni);
        if (persons.length === 1) {
          this.applyPadronPerson(persons[0]);
        } else {
          this.padron.persons = persons;
        }
      } catch (err) {
        this.$message({ type: "error", message: lookupErrorMessage(err) });
      } finally {
        this.padron.searching = false;
      }
    },
    /**
     * @description UC-REG-317 — Elige una de las personas encontradas.
     * @param {number} index
     */
    choosePadronPerson(index) {
      const person = this.padron.persons[index];
      if (person) this.applyPadronPerson(person);
    },
    /**
     * @description UC-REG-317 — Vuelca la persona del padrón en el form.
     * @param {Object} person
     */
    applyPadronPerson(person) {
      Object.assign(this.patient, personToQuickForm(person));
      this.padron.applied = true;
      if (person.deceased) {
        this.$message({
          type: "warning",
          message: "Atención: la persona figura fallecida en el padrón.",
        });
      }
    },
    /**
     * @description UC-REG-317 — Etiqueta de una persona para el selector.
     * @param {Object} person
     * @returns {string}
     */
    padronPersonLabel(person) {
      return personLabel(person);
    },
  },
};
</script>

<template lang="pug">
.patient-quick-form
  .patient-quick-form__header
    button.btn-ghost.btn-sm(type="button" @click="cancel")
      i.el-icon-arrow-left
      |  Volver
    h4.patient-quick-form__title Crear paciente
  ValidationObserver(ref="observer" v-slot="{}")
    form.patient-quick-form__body(@submit.prevent="submit" novalidate)
      .form-row
        ValidationProvider(name="Nombre" rules="required" v-slot="{ errors }" tag="div" class="form-field")
          label.label Nombre *
          el-input(v-model="patient.firstName" placeholder="Nombre" size="small")
          span.has-error(v-if="errors[0]") {{ errors[0] }}
        ValidationProvider(name="Apellido" rules="required" v-slot="{ errors }" tag="div" class="form-field")
          label.label Apellido *
          el-input(v-model="patient.lastName" placeholder="Apellido" size="small")
          span.has-error(v-if="errors[0]") {{ errors[0] }}
      .form-row
        .form-field.form-field--narrow
          label.label Tipo doc
          el-select(v-model="patient.governmentId.type" size="small")
            el-option(v-for="t in docTypes" :key="t.value" :value="t.value" :label="t.label")
        ValidationProvider(name="Número" rules="required" v-slot="{ errors }" tag="div" class="form-field")
          label.label Número *
          el-input(v-model="patient.governmentId.number" placeholder="29384756" size="small")
          span.has-error(v-if="errors[0]") {{ errors[0] }}
      //- UC-REG-317 — autocompletar desde el padrón del Municipio (Campana).
      .form-row(v-if="padronLookupEnabled")
        .form-field
          el-button.patient-quick-form__padron-btn(size="small" type="primary" plain :loading="padron.searching" @click="searchPadron") Buscar en el padrón
          span.patient-quick-form__padron-hint(v-if="padron.applied") Datos tomados del padrón del Municipio.
      .form-row(v-if="padron.persons.length > 1")
        .form-field
          label.label Varias personas con ese DNI: elegí la correcta
          el-select(v-model="padron.selected" placeholder="Elegí una persona" size="small" @change="choosePadronPerson")
            el-option(v-for="(p, idx) in padron.persons" :key="p.cuil || idx" :value="idx" :label="padronPersonLabel(p)")
      .form-row
        .form-field
          label.label Fecha de nacimiento
          el-date-picker(v-model="patient.bornAt" type="date" placeholder="dd/MM/yyyy" format="dd/MM/yyyy" value-format="yyyy-MM-dd" size="small" style="width:100%")
        .form-field
          label.label Género
          el-select(v-model="patient.gender" placeholder="—" size="small" clearable)
            el-option(v-for="g in genders" :key="g.value" :value="g.value" :label="g.label")
      .form-row
        .form-field
          label.label Email
          el-input(v-model="patient.email" placeholder="opcional" size="small" type="email")
        .form-field
          label.label Teléfono
          el-input(v-model="patient.phone" placeholder="opcional" size="small")
      .patient-quick-form__actions
        el-button(type="primary" size="small" :loading="isSubmitting" @click="submit") Crear y seleccionar
</template>

<style lang="scss" scoped>
// UC-REG-317 — búsqueda en el padrón del Municipio.
.patient-quick-form__padron-hint {
  margin-left: 10px;
  font-size: 12px;
  color: #6b7280;
}
.patient-quick-form {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 2px;

    &--narrow {
      max-width: 110px;
    }
  }

  .label {
    font-size: 12px;
    color: #5e6770;
  }

  .has-error {
    font-size: 11px;
    color: #d9534f;
  }

  &__actions {
    margin-top: 8px;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
